// Compute initial voxel bounds based on approximate physical screen size
// and a simple density heuristic.

import { MORTON_MAX_COORD } from '@/voxelMath'
import {
  DEFAULT_VOXELS_PER_CM,
  INITIAL_BOUNDS_HEIGHT_RATIO,
  INITIAL_BOUNDS_MIN_XZ,
  INITIAL_BOUNDS_MIN_Y,
  USE_DEVICE_PIXEL_RATIO_FOR_CM,
} from '@/core/constants'

export type BoundsBaseAspect = 'screen' | 'square'

export type ComputeBoundsFromWindowOptions = {
  baseAspect?: BoundsBaseAspect
  heightRatio?: number
  minHeight?: number
}

export function computeBoundsFromWindow(
  voxelsPerCm: number = DEFAULT_VOXELS_PER_CM,
  options: ComputeBoundsFromWindowOptions = {},
): { x: number; y: number; z: number } {
  let w = 1280, h = 800, dpr = 1
  if (typeof window !== 'undefined') {
    w = window.innerWidth || w
    h = window.innerHeight || h
    dpr = window.devicePixelRatio || 1
  }

  const PX_PER_IN = 96
  const CM_PER_IN = 2.54
  const pxPerCm = (USE_DEVICE_PIXEL_RATIO_FOR_CM ? PX_PER_IN * dpr : PX_PER_IN) / CM_PER_IN
  const widthCm = w / pxPerCm
  const heightCm = h / pxPerCm
  const screenX = widthCm * voxelsPerCm
  const screenZ = heightCm * voxelsPerCm
  const squareBase = Math.min(screenX, screenZ)
  const rawX = options.baseAspect === 'square' ? squareBase : screenX
  const rawZ = options.baseAspect === 'square' ? squareBase : screenZ
  const baseMin = Math.max(0.001, Math.min(rawX, rawZ))
  const baseMax = Math.max(rawX, rawZ)
  let baseScale = Math.max(1, INITIAL_BOUNDS_MIN_XZ / baseMin)
  if (baseMax * baseScale > MORTON_MAX_COORD) baseScale = MORTON_MAX_COORD / baseMax
  const heightRatio = options.heightRatio ?? INITIAL_BOUNDS_HEIGHT_RATIO
  const minHeight = options.minHeight ?? INITIAL_BOUNDS_MIN_Y

  // Clamp to Morton encoding maximum (0-511 per axis)
  const x = Math.min(MORTON_MAX_COORD, Math.max(INITIAL_BOUNDS_MIN_XZ, Math.round(rawX * baseScale)))
  const z = Math.min(MORTON_MAX_COORD, Math.max(INITIAL_BOUNDS_MIN_XZ, Math.round(rawZ * baseScale)))
  const y = Math.min(MORTON_MAX_COORD, Math.max(minHeight, Math.round(Math.min(x, z) * heightRatio)))

  return { x, y, z }
}

/**
 * Bounds with a fixed shorter base side (the Huge start size): the base keeps the aspect that
 * `computeBoundsFromWindow` would give for these options, scaled so min(x, z) = `minSide`;
 * the height follows the same height ratio. Clamped to the Morton coordinate limit.
 */
export function computeBoundsForMinSide(
  minSide: number,
  options: ComputeBoundsFromWindowOptions = {},
): { x: number; y: number; z: number } {
  const aspect = computeBoundsFromWindow(DEFAULT_VOXELS_PER_CM, options)
  const shorter = Math.max(1, Math.min(aspect.x, aspect.z))
  const scale = minSide / shorter
  const x = Math.min(MORTON_MAX_COORD, Math.max(INITIAL_BOUNDS_MIN_XZ, Math.round(aspect.x * scale)))
  const z = Math.min(MORTON_MAX_COORD, Math.max(INITIAL_BOUNDS_MIN_XZ, Math.round(aspect.z * scale)))
  const heightRatio = options.heightRatio ?? INITIAL_BOUNDS_HEIGHT_RATIO
  const minHeight = options.minHeight ?? INITIAL_BOUNDS_MIN_Y
  const y = Math.min(MORTON_MAX_COORD, Math.max(minHeight, Math.round(Math.min(x, z) * heightRatio)))
  return { x, y, z }
}
